import type { ClearinghouseStateResponse } from "@nktkas/hyperliquid"
import { HL_MAX_MARKETS } from "@src/features/trade/hyperliquid/constants/config"
import { getMarketMetadata } from "@src/features/trade/hyperliquid/constants/marketMetadata"
import {
  type HLMarketAnnotation,
  MAIN_DEX,
  TRADE_XYZ_DEX,
  deriveOrderAssetId,
  getDexFromCoin,
  getDexLabel,
  isRwaMarket,
  normalizeMarketDisplay,
} from "@src/features/trade/hyperliquid/marketIdentity"
import type {
  HLAccountAbstraction,
  HLAccountState,
  HLActiveAssetData,
  HLAssetContext,
  HLDexName,
  HLFill,
  HLMarketInfo,
  HLOpenOrder,
  HLPortfolio,
  HLPortfolioBucket,
  HLPortfolioPeriod,
  HLPosition,
  HLSpotUsdcBalance,
  HLTradingBalance,
  Result,
} from "@src/features/trade/hyperliquid/types"
import { logger } from "@src/utils/logger"
import type { Address } from "viem"
import { getHyperliquidInfoClient } from "./hyperliquidClient"

type DexRequestOptions = {
  dex?: HLDexName
  signal?: AbortSignal
}

type TradingBalanceOptions = DexRequestOptions & {
  knownAbstraction?: HLAccountAbstraction
}

type RawMetaAsset = {
  name: string
  maxLeverage: number
  szDecimals: number
  isDelisted?: true
}

type RawAssetCtx = {
  midPx?: string | null
  prevDayPx?: string
  dayNtlVlm?: string
  openInterest?: string
  funding?: string
}

type MarketSource = {
  dex: HLDexName
  dexIndex: number
  meta: { universe: RawMetaAsset[] }
  ctxs: RawAssetCtx[]
}

type TradingBalanceAvailability = Pick<
  HLTradingBalance,
  "available" | "availableSource" | "source"
>

const MARKET_SOURCE_TIMEOUT_MS = 5_000

class MarketCatalogSourceTimeoutError extends Error {
  constructor(source: HLDexName) {
    super(`Timed out loading ${getDexLabel(source)} markets`)
    this.name = "MarketCatalogSourceTimeoutError"
  }
}

function createAbortError() {
  if (typeof DOMException !== "undefined") {
    return new DOMException("The operation was aborted", "AbortError")
  }
  const error = new Error("The operation was aborted")
  error.name = "AbortError"
  return error
}

export type HLCandle = {
  time: number
  value: number
  open: number
  high: number
  low: number
  close: number
}

async function wrapResult<T>(
  name: string,
  fn: () => Promise<Result<T>>,
  logCtx?: Record<string, unknown>
): Promise<Result<T>> {
  try {
    return await fn()
  } catch (e) {
    if (isCancellation(e)) throw e
    const message = e instanceof Error ? e.message : String(e)
    logger.error(`HL ${name} failed: ${message}`, {
      ...(logCtx ?? {}),
      errorName: e instanceof Error ? e.name : typeof e,
      error: String(e),
    })
    return { ok: false, error: message || "Unknown error" }
  }
}

function isCancellation(e: unknown): boolean {
  // Walk the cause chain: native fetch aborts surface as AbortError /
  // DOMException; the HL SDK wraps them in HttpRequestError but preserves
  // the original via `cause`.
  let current: unknown = e
  for (let depth = 0; depth < 5 && current != null; depth++) {
    if (current instanceof Error && current.name === "AbortError") return true
    if (
      typeof DOMException !== "undefined" &&
      current instanceof DOMException &&
      current.name === "AbortError"
    ) {
      return true
    }
    current = current instanceof Error ? current.cause : null
  }
  return false
}

function maskedAddress(user: string): string {
  return `${user.slice(0, 6)}...${user.slice(-4)}`
}

function normalizeDexOptions(options: DexRequestOptions = {}): {
  dex: HLDexName
  signal?: AbortSignal
} {
  return { dex: options.dex ?? MAIN_DEX, signal: options.signal }
}

function buildUserDexParams(user: string, dex: HLDexName) {
  return dex === MAIN_DEX ? { user } : { user, dex }
}

function usesSpotUsdcBalance(abstraction: HLAccountAbstraction) {
  return abstraction === "unifiedAccount" || abstraction === "portfolioMargin"
}

function deriveTradingBalanceAvailability({
  accountState,
  mainAccountState,
  spotUsdc,
  usesSpotUsdc,
}: {
  accountState: HLAccountState
  mainAccountState: HLAccountState | null
  spotUsdc: HLSpotUsdcBalance | null
  usesSpotUsdc: boolean
}): TradingBalanceAvailability {
  if (usesSpotUsdc && spotUsdc != null) {
    return {
      available: spotUsdc.available,
      availableSource: "spotUsdc",
      source: "spotUsdc",
    }
  }

  if (mainAccountState != null) {
    return {
      available: mainAccountState.withdrawable,
      availableSource: "mainPerps",
      source: "perps",
    }
  }

  return {
    available: accountState.withdrawable,
    availableSource: "selectedPerps",
    source: "perps",
  }
}

export function updateTradingBalanceAccountState(
  tradingBalance: HLTradingBalance,
  accountState: HLAccountState
): HLTradingBalance {
  if (tradingBalance.availableSource === "selectedPerps") {
    return {
      ...tradingBalance,
      accountState,
      available: accountState.withdrawable,
    }
  }

  return {
    ...tradingBalance,
    accountState,
  }
}

async function getMarketSources(signal?: AbortSignal): Promise<MarketSource[]> {
  const [mainSource, tradeXyzSource] = await Promise.all([
    getMainMarketSource(signal),
    getRequiredTradeXyzSourceWithTimeout(signal),
  ])
  return [mainSource, tradeXyzSource]
}

async function getMainMarketSource(
  signal?: AbortSignal
): Promise<MarketSource> {
  const [meta, ctxs] = await getHyperliquidInfoClient().metaAndAssetCtxs(signal)
  return {
    dex: MAIN_DEX,
    dexIndex: 0,
    meta,
    ctxs,
  }
}

async function getRequiredTradeXyzSourceWithTimeout(
  signal?: AbortSignal
): Promise<MarketSource> {
  const controller = new AbortController()
  const timeoutError = new MarketCatalogSourceTimeoutError(TRADE_XYZ_DEX)
  let rejectTimedOut: (reason: unknown) => void = () => undefined
  const timedOut = new Promise<never>((_, reject) => {
    rejectTimedOut = reject
  })

  let timeout: NodeJS.Timeout | number | undefined
  timeout = setTimeout(() => {
    controller.abort(timeoutError)
    rejectTimedOut(timeoutError)
  }, MARKET_SOURCE_TIMEOUT_MS)

  const abortFromParent = () => {
    const reason = isCancellation(signal?.reason)
      ? signal?.reason
      : createAbortError()
    controller.abort(reason)
    rejectTimedOut(reason)
  }
  if (signal?.aborted) abortFromParent()
  else signal?.addEventListener("abort", abortFromParent, { once: true })

  const source = getTradeXyzSource(controller.signal)

  try {
    return await Promise.race([source, timedOut])
  } finally {
    if (timeout != null) clearTimeout(timeout)
    signal?.removeEventListener("abort", abortFromParent)
    source.catch(() => undefined)
  }
}

async function getTradeXyzSource(signal?: AbortSignal): Promise<MarketSource> {
  const dexIndex = await findTradeXyzDexIndex(signal)

  const [meta, ctxs] = await getHyperliquidInfoClient().metaAndAssetCtxs(
    { dex: TRADE_XYZ_DEX },
    signal
  )
  return { dex: TRADE_XYZ_DEX, dexIndex, meta, ctxs }
}

// The perp-dex roster is deployment-immutable, so once we've located trade.xyz
// we memoize its index instead of re-fetching perpDexs() on every getMarkets()
// and getAssetMeta() (the order-prep path). We only cache the positive result:
// a "not found" stays uncached so a transient outage self-heals on recovery.
// This lives in the service layer (React-free), so it can't use React Query —
// callers' results are already cached at the hook layer; this dedupes the
// internal perpDexs() round-trip shared across those separate cached queries.
let cachedTradeXyzDexIndex: number | null = null

// Exposed for tests to reset the module-level memo between cases.
export function __resetTradeXyzDexIndexCache(): void {
  cachedTradeXyzDexIndex = null
}

async function findTradeXyzDexIndex(signal?: AbortSignal): Promise<number> {
  if (cachedTradeXyzDexIndex != null) return cachedTradeXyzDexIndex

  const dexs = await getHyperliquidInfoClient().perpDexs(signal)
  const index = dexs.findIndex((dex) => dex?.name === TRADE_XYZ_DEX)
  if (index > 0) {
    cachedTradeXyzDexIndex = index
    return index
  }
  throw new Error(`Perp dex ${getDexLabel(TRADE_XYZ_DEX)} not found`)
}

async function getDexIndex(
  dex: HLDexName,
  signal?: AbortSignal
): Promise<number> {
  if (dex === MAIN_DEX) return 0
  return findTradeXyzDexIndex(signal)
}

function getMetaForDex(dex: HLDexName, signal?: AbortSignal) {
  const client = getHyperliquidInfoClient()
  return dex === MAIN_DEX ? client.meta(signal) : client.meta({ dex }, signal)
}

function getMetaAndAssetCtxsForDex(dex: HLDexName, signal?: AbortSignal) {
  const client = getHyperliquidInfoClient()
  return dex === MAIN_DEX
    ? client.metaAndAssetCtxs(signal)
    : client.metaAndAssetCtxs({ dex }, signal)
}

async function getPerpAnnotations(
  signal?: AbortSignal
): Promise<Map<string, HLMarketAnnotation>> {
  const client = getHyperliquidInfoClient()
  const annotations = new Map<string, HLMarketAnnotation>()

  try {
    const raw = await client.config_.transport.request<unknown>(
      "info",
      { type: "perpConciseAnnotations" },
      signal
    )
    addRawAnnotations(annotations, raw)
  } catch (e) {
    if (isCancellation(e)) throw e
  }

  if (annotations.size > 0) return annotations

  try {
    const categories = await client.perpCategories(signal)
    for (const [coin, category] of categories) {
      annotations.set(coin, { category })
    }
  } catch (e) {
    if (isCancellation(e)) throw e
  }

  return annotations
}

function addRawAnnotations(
  annotations: Map<string, HLMarketAnnotation>,
  raw: unknown
) {
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      addRawAnnotationEntry(annotations, entry)
    }
    return
  }

  if (raw && typeof raw === "object") {
    for (const [coin, annotation] of Object.entries(raw)) {
      if (coin !== "annotations") {
        setAnnotation(annotations, coin, annotation)
      } else {
        addRawAnnotations(annotations, annotation)
      }
    }
  }
}

function addRawAnnotationEntry(
  annotations: Map<string, HLMarketAnnotation>,
  entry: unknown
) {
  if (Array.isArray(entry) && typeof entry[0] === "string") {
    setAnnotation(annotations, entry[0], entry[1])
    return
  }

  if (entry && typeof entry === "object") {
    const coin = getStringField(entry, "coin") ?? getStringField(entry, "name")
    if (coin) setAnnotation(annotations, coin, entry)
  }
}

function setAnnotation(
  annotations: Map<string, HLMarketAnnotation>,
  coin: string,
  raw: unknown
) {
  if (!raw || typeof raw !== "object") return
  const category = getStringField(raw, "category")
  const displayName = getStringField(raw, "displayName")
  const description = getStringField(raw, "description")
  const keywords = Array.isArray((raw as { keywords?: unknown }).keywords)
    ? (raw as { keywords: unknown[] }).keywords.filter(
        (keyword): keyword is string => typeof keyword === "string"
      )
    : undefined

  annotations.set(coin, {
    category,
    displayName,
    description,
    keywords,
  })
}

function getStringField(raw: object, key: string): string | undefined {
  const value = (raw as Record<string, unknown>)[key]
  return typeof value === "string" ? value : undefined
}

function buildMarketInfo(params: {
  asset: RawMetaAsset
  annotations: Map<string, HLMarketAnnotation>
  ctx?: RawAssetCtx
  dex: HLDexName
  dexIndex: number
  indexInDex: number
}): HLMarketInfo {
  const annotation = params.annotations.get(params.asset.name)
  const fallbackName =
    params.dex === MAIN_DEX
      ? getMarketMetadata(params.asset.name).name
      : undefined
  const display = normalizeMarketDisplay({
    coin: params.asset.name,
    annotation,
    fallbackName,
  })

  return {
    name: params.asset.name,
    displaySymbol: display.displaySymbol,
    displayName: display.displayName,
    dex: params.dex,
    dexLabel: getDexLabel(params.dex),
    dexIndex: params.dexIndex,
    indexInDex: params.indexInDex,
    orderAssetId: deriveOrderAssetId({
      dexIndex: params.dexIndex,
      indexInDex: params.indexInDex,
    }),
    category: annotation?.category,
    keywords: display.keywords,
    isRwa: isRwaMarket({ dex: params.dex, category: annotation?.category }),
    maxLeverage: params.asset.maxLeverage,
    szDecimals: params.asset.szDecimals,
    midPrice: params.ctx?.midPx ?? null,
    prevDayPx: params.ctx?.prevDayPx ?? null,
    dayNtlVlm: params.ctx?.dayNtlVlm ?? null,
    openInterest: params.ctx?.openInterest ?? null,
    funding: params.ctx?.funding ?? null,
  }
}

function selectMarketCatalog(markets: HLMarketInfo[]): HLMarketInfo[] {
  if (markets.length <= HL_MAX_MARKETS) return markets

  const rwaMarkets = markets.filter((market) => market.isRwa)
  const nonRwaLimit = Math.max(HL_MAX_MARKETS - rwaMarkets.length, 0)
  const activeNonRwaMarkets = markets
    .filter((market) => !market.isRwa)
    .slice(0, nonRwaLimit)

  return [...activeNonRwaMarkets, ...rwaMarkets]
}

export async function getAccountState(
  user: string,
  options: DexRequestOptions = {}
): Promise<Result<HLAccountState>> {
  const { dex, signal } = normalizeDexOptions(options)
  return wrapResult(
    "getAccountState",
    async () => {
      const data = await getHyperliquidInfoClient().clearinghouseState(
        buildUserDexParams(user, dex),
        signal
      )
      return { ok: true, data: mapAccountState(data) }
    },
    { user: maskedAddress(user) }
  )
}

export async function getTradingBalance(
  user: Address,
  options: TradingBalanceOptions = {}
): Promise<Result<HLTradingBalance>> {
  const { dex, signal } = normalizeDexOptions(options)
  return wrapResult(
    "getTradingBalance",
    async () => {
      const client = getHyperliquidInfoClient()
      const [rawAccountState, abstraction] = await Promise.all([
        client.clearinghouseState(buildUserDexParams(user, dex), signal),
        options.knownAbstraction ?? client.userAbstraction({ user }, signal),
      ])

      const accountState = mapAccountState(rawAccountState)
      const usesUnifiedBalance = usesSpotUsdcBalance(abstraction)
      const shouldFetchSpotUsdc =
        usesUnifiedBalance ||
        (dex === MAIN_DEX &&
          (abstraction === "default" || abstraction === "disabled"))
      const shouldUseMainPerpsBalance = dex !== MAIN_DEX && !usesUnifiedBalance
      const spotUsdc = shouldFetchSpotUsdc
        ? mapSpotUsdcBalance(
            await client.spotClearinghouseState({ user }, signal)
          )
        : null
      const mainAccountState = shouldUseMainPerpsBalance
        ? mapAccountState(await client.clearinghouseState({ user }, signal))
        : null
      const availability = deriveTradingBalanceAvailability({
        accountState,
        mainAccountState,
        spotUsdc,
        usesSpotUsdc: usesUnifiedBalance,
      })

      return {
        ok: true,
        data: {
          accountState,
          abstraction,
          ...availability,
          spotUsdc,
        },
      }
    },
    { user: maskedAddress(user) }
  )
}

export async function getUserAbstraction(
  user: Address,
  signal?: AbortSignal
): Promise<Result<HLAccountAbstraction>> {
  return wrapResult(
    "getUserAbstraction",
    async () => {
      const data = await getHyperliquidInfoClient().userAbstraction(
        { user },
        signal
      )
      return { ok: true, data }
    },
    { user: maskedAddress(user) }
  )
}

// Builder dexes (e.g. trade.xyz) require the account to use a collateral mode
// that can margin their encoded markets; "default"/"disabled" cannot.
export function supportsBuilderDexCollateral(
  abstraction: HLAccountAbstraction
): boolean {
  return (
    abstraction === "dexAbstraction" ||
    abstraction === "unifiedAccount" ||
    abstraction === "portfolioMargin"
  )
}

export async function getPortfolio(
  user: Address,
  signal?: AbortSignal
): Promise<Result<HLPortfolio>> {
  return wrapResult("getPortfolio", async () => {
    const data = await getHyperliquidInfoClient().portfolio({ user }, signal)
    return {
      ok: true,
      data: mapPortfolio(data as unknown as [string, HLPortfolioBucket][]),
    }
  })
}

export async function getMarkets(
  signal?: AbortSignal
): Promise<Result<HLMarketInfo[]>> {
  return wrapResult("getMarkets", async () => {
    const [sources, annotations] = await Promise.all([
      getMarketSources(signal),
      getPerpAnnotations(signal),
    ])
    const markets: HLMarketInfo[] = []

    for (const source of sources) {
      for (const [indexInDex, asset] of source.meta.universe.entries()) {
        if (asset.isDelisted) continue
        markets.push(
          buildMarketInfo({
            asset,
            annotations,
            ctx: source.ctxs[indexInDex],
            dex: source.dex,
            dexIndex: source.dexIndex,
            indexInDex,
          })
        )
      }
    }

    markets.sort((a, b) => Number(b.dayNtlVlm ?? 0) - Number(a.dayNtlVlm ?? 0))
    return { ok: true, data: selectMarketCatalog(markets) }
  })
}

export async function getMidPrices(
  signal?: AbortSignal
): Promise<Result<Record<string, string>>> {
  return wrapResult("getMidPrices", async () => {
    const client = getHyperliquidInfoClient()
    const [main, tradeXyz] = await Promise.all([
      client.allMids(signal),
      client.allMids({ dex: TRADE_XYZ_DEX }, signal).catch((e) => {
        if (isCancellation(e)) throw e
        return {}
      }),
    ])
    return { ok: true, data: { ...main, ...tradeXyz } }
  })
}

export async function getOpenOrders(
  user: string,
  options: DexRequestOptions = {}
): Promise<Result<HLOpenOrder[]>> {
  const { dex, signal } = normalizeDexOptions(options)
  return wrapResult("getOpenOrders", async () => {
    const data = await getHyperliquidInfoClient().frontendOpenOrders(
      buildUserDexParams(user, dex),
      signal
    )
    return { ok: true, data: data as unknown as HLOpenOrder[] }
  })
}

export async function getUserFills(
  user: Address,
  opts: { coin?: string; limit?: number } = {},
  signal?: AbortSignal
): Promise<Result<HLFill[]>> {
  return wrapResult("getUserFills", async () => {
    const data = await getHyperliquidInfoClient().userFills(
      { user, aggregateByTime: true },
      signal
    )
    const fills = opts.coin
      ? data.filter((f) => f.coin === opts.coin)
      : [...data]
    fills.sort((a, b) => b.time - a.time)
    const limited = opts.limit ? fills.slice(0, opts.limit) : fills
    return { ok: true, data: limited as unknown as HLFill[] }
  })
}

export async function getCandleData(
  coin: string,
  interval:
    | "1m"
    | "3m"
    | "5m"
    | "15m"
    | "1h"
    | "4h"
    | "8h"
    | "1d"
    | "1w" = "15m",
  lookbackMs = 24 * 60 * 60 * 1000,
  signal?: AbortSignal,
  endTime = Date.now()
): Promise<Result<HLCandle[]>> {
  return wrapResult("getCandleData", async () => {
    const data = await getHyperliquidInfoClient().candleSnapshot(
      { coin, interval, startTime: endTime - lookbackMs, endTime },
      signal
    )
    return {
      ok: true,
      data: data
        .map((c) => ({
          time: c.t,
          value: Number(c.c),
          open: Number(c.o),
          high: Number(c.h),
          low: Number(c.l),
          close: Number(c.c),
        }))
        .sort((a, b) => a.time - b.time),
    }
  })
}

export async function getAssetMeta(
  coin: string,
  signal?: AbortSignal
): Promise<
  Result<{ orderAssetId: number; szDecimals: number; maxLeverage: number }>
> {
  return wrapResult("getAssetMeta", async () => {
    const dex = getDexFromCoin(coin)
    const dexIndex = await getDexIndex(dex, signal)
    const meta = await getMetaForDex(dex, signal)
    for (const [i, asset] of meta.universe.entries()) {
      if (asset.name === coin) {
        return {
          ok: true,
          data: {
            orderAssetId: deriveOrderAssetId({ dexIndex, indexInDex: i }),
            szDecimals: asset.szDecimals,
            maxLeverage: asset.maxLeverage,
          },
        }
      }
    }
    return { ok: false, error: `Asset ${coin} not found` }
  })
}

export async function getAssetContext(
  coin: string,
  signal?: AbortSignal
): Promise<Result<HLAssetContext>> {
  return wrapResult("getAssetContext", async () => {
    const dex = getDexFromCoin(coin)
    const [meta, ctxs] = await getMetaAndAssetCtxsForDex(dex, signal)
    const idx = meta.universe.findIndex((asset) => asset.name === coin)
    if (idx === -1) {
      return { ok: false, error: `Asset ${coin} not found` }
    }
    const ctx = ctxs[idx]
    if (!ctx) {
      return { ok: false, error: `Asset context ${coin} not found` }
    }
    return {
      ok: true,
      data: {
        prevDayPx: ctx.prevDayPx,
        dayNtlVlm: ctx.dayNtlVlm,
        dayBaseVlm: ctx.dayBaseVlm,
        markPx: ctx.markPx,
        midPx: ctx.midPx,
        funding: ctx.funding,
        openInterest: ctx.openInterest,
        premium: ctx.premium,
        oraclePx: ctx.oraclePx,
      },
    }
  })
}

export async function getMaxBuilderFee(
  user: Address,
  builder: Address,
  signal?: AbortSignal
): Promise<Result<number>> {
  return wrapResult("getMaxBuilderFee", async () => {
    const data = await getHyperliquidInfoClient().maxBuilderFee(
      { user, builder },
      signal
    )
    return { ok: true, data }
  })
}

export async function getActiveAssetData(
  user: Address,
  coin: string,
  signal?: AbortSignal
): Promise<Result<HLActiveAssetData>> {
  return wrapResult("getActiveAssetData", async () => {
    const data = await getHyperliquidInfoClient().activeAssetData(
      { user, coin },
      signal
    )
    return {
      ok: true,
      data: {
        coin: data.coin,
        leverage: data.leverage,
        markPx: data.markPx,
      },
    }
  })
}

export function mapAccountState(
  raw: ClearinghouseStateResponse
): HLAccountState {
  const positions: HLPosition[] = raw.assetPositions.map((ap) => ({
    coin: ap.position.coin,
    szi: ap.position.szi,
    entryPx: ap.position.entryPx,
    positionValue: ap.position.positionValue,
    unrealizedPnl: ap.position.unrealizedPnl,
    returnOnEquity: ap.position.returnOnEquity,
    leverage: ap.position.leverage,
    liquidationPx: ap.position.liquidationPx,
    marginUsed: ap.position.marginUsed,
  }))

  return {
    positions,
    marginSummary: raw.marginSummary,
    crossMarginSummary: raw.crossMarginSummary,
    withdrawable: raw.withdrawable,
    crossMaintenanceMarginUsed: raw.crossMaintenanceMarginUsed,
  }
}

function mapPortfolio(raw: [string, HLPortfolioBucket][]): HLPortfolio {
  const portfolio: HLPortfolio = {}

  for (const [period, bucket] of raw) {
    if (isPortfolioPeriod(period)) {
      portfolio[period] = bucket
    }
  }

  return portfolio
}

function mapSpotUsdcBalance(raw: {
  balances: Array<{ coin: string; total: string; hold: string }>
}): HLSpotUsdcBalance | null {
  const usdc = raw.balances.find((balance) => balance.coin === "USDC")
  if (!usdc) return null

  return {
    total: usdc.total,
    hold: usdc.hold,
    available: subtractDecimalStrings(usdc.total, usdc.hold),
  }
}

function subtractDecimalStrings(minuend: string, subtrahend: string): string {
  const [leftWhole, leftFraction = ""] = minuend.split(".")
  const [rightWhole, rightFraction = ""] = subtrahend.split(".")
  const decimals = Math.max(leftFraction.length, rightFraction.length)
  const scale = 10n ** BigInt(decimals)
  const left =
    BigInt(leftWhole) * scale +
    BigInt(leftFraction.padEnd(decimals, "0") || "0")
  const right =
    BigInt(rightWhole) * scale +
    BigInt(rightFraction.padEnd(decimals, "0") || "0")
  const result = left > right ? left - right : 0n
  const whole = result / scale
  const fraction = (result % scale).toString().padStart(decimals, "0")

  if (decimals === 0) return whole.toString()

  const trimmedFraction = fraction.replace(/0+$/, "")
  return trimmedFraction ? `${whole}.${trimmedFraction}` : whole.toString()
}

function isPortfolioPeriod(period: string): period is HLPortfolioPeriod {
  return (
    period === "day" ||
    period === "week" ||
    period === "month" ||
    period === "allTime" ||
    period === "perpDay" ||
    period === "perpWeek" ||
    period === "perpMonth" ||
    period === "perpAllTime"
  )
}
